/* factory-graphics-manifest.js - schema helpers for the factory graphics runtime. */
(function () {
  const MANIFEST_VERSION = "factory-graphics-manifest.v1";
  const DEFAULT_MANIFEST = "production-v1";
  const DEFAULT_BINDINGS = "default";
  const MANIFEST_BASE_URL = "/assets/factory/manifests/";
  const BINDING_BASE_URL = "/assets/factory/bindings/";
  const DEFAULT_QUALITY = Object.freeze({
    level: "balanced",
    devicePixelRatio: 1,
    imageSmoothing: true,
    shadows: true,
    lighting: true,
    particles: true,
    qaOverlay: false,
    maxSpriteScale: 2,
  });
  const DEFAULT_SCENE = Object.freeze({
    width: 1420,
    height: 700,
    tileW: 52,
    tileH: 26,
    originX: 530,
    originY: 76,
  });
  const LAYER_ORDER = Object.freeze(["base", "machines", "actors", "foreground", "lighting", "qa"]);

  function object(value) {
    return value && typeof value === "object" && !Array.isArray(value) ? value : {};
  }

  function array(value) {
    return Array.isArray(value) ? value : [];
  }

  function cleanString(value, fallback) {
    const clean = String(value || "").trim();
    return clean || fallback || "";
  }

  function clone(value) {
    if (value === null || value === undefined) return value;
    try { return JSON.parse(JSON.stringify(value)); } catch (_) { return value; }
  }

  function freezeDeep(value) {
    if (!value || typeof value !== "object" || Object.isFrozen(value)) return value;
    Object.keys(value).forEach((key) => freezeDeep(value[key]));
    return Object.freeze(value);
  }

  function manifestUrl(name) {
    const clean = cleanString(name, DEFAULT_MANIFEST);
    if (/^https?:\/\//i.test(clean) || clean.startsWith("/")) return clean;
    return `${MANIFEST_BASE_URL}${encodeURIComponent(clean)}.json`;
  }

  function bindingsUrl(name) {
    const clean = cleanString(name, DEFAULT_BINDINGS);
    if (/^https?:\/\//i.test(clean) || clean.startsWith("/")) return clean;
    return `${BINDING_BASE_URL}${encodeURIComponent(clean)}.json`;
  }

  function mergeQualitySettings(base, override) {
    const next = { ...DEFAULT_QUALITY, ...object(base), ...object(override) };
    const level = cleanString(next.level, "balanced").toLowerCase();
    if (level === "low") {
      next.imageSmoothing = next.imageSmoothing !== false;
      next.shadows = false;
      next.lighting = false;
      next.particles = false;
      next.maxSpriteScale = Math.min(Number(next.maxSpriteScale) || 1, 1.25);
    } else if (level === "high") {
      next.shadows = next.shadows !== false;
      next.lighting = next.lighting !== false;
      next.particles = next.particles !== false;
      next.maxSpriteScale = Math.max(Number(next.maxSpriteScale) || 2, 2);
    }
    next.devicePixelRatio = Math.max(0.5, Math.min(Number(next.devicePixelRatio) || 1, 3));
    next.maxSpriteScale = Math.max(0.5, Math.min(Number(next.maxSpriteScale) || 1, 4));
    next.qaOverlay = !!next.qaOverlay;
    return freezeDeep(next);
  }

  function normalizeImage(id, image) {
    const source = object(image);
    return {
      id: cleanString(source.id, id),
      src: cleanString(source.src || source.url, ""),
      width: Number(source.width) || 0,
      height: Number(source.height) || 0,
      pixelRatio: Number(source.pixelRatio) || 1,
      required: source.required === true,
      role: cleanString(source.role, ""),
    };
  }

  function normalizeImages(images) {
    const source = object(images);
    return Object.keys(source).reduce((result, key) => {
      result[key] = normalizeImage(key, source[key]);
      return result;
    }, {});
  }

  function normalizeLayer(id, layer) {
    const source = object(layer);
    return {
      id: cleanString(source.id, id),
      order: Number(source.order) || 0,
      visible: source.visible !== false,
      blendMode: cleanString(source.blendMode || source.compositeOperation, "source-over"),
      opacity: Math.max(0, Math.min(Number(source.opacity ?? 1) || 0, 1)),
      image: cleanString(source.image || source.imageKey, ""),
      fill: cleanString(source.fill, ""),
      parallax: Number(source.parallax) || 0,
      metadata: object(source.metadata),
    };
  }

  function normalizeLayers(layers) {
    const source = object(layers);
    const base = LAYER_ORDER.reduce((result, id, index) => {
      result[id] = normalizeLayer(id, { id, order: index, visible: true });
      return result;
    }, {});
    Object.keys(source).forEach((key) => {
      base[key] = normalizeLayer(key, source[key]);
    });
    return base;
  }

  function normalizeFrame(frame) {
    const source = object(frame);
    return {
      x: Number(source.x) || 0,
      y: Number(source.y) || 0,
      w: Number(source.w || source.width) || 0,
      h: Number(source.h || source.height) || 0,
      durationMs: Math.max(16, Number(source.durationMs || source.duration || 120) || 120),
      anchorX: Number(source.anchorX ?? 0.5),
      anchorY: Number(source.anchorY ?? 1),
    };
  }

  function normalizeAnimation(id, animation) {
    const source = object(animation);
    return {
      id: cleanString(source.id, id),
      type: cleanString(source.type, "placeholder"),
      image: cleanString(source.image || source.imageKey, ""),
      frameWidth: Number(source.frameWidth) || 0,
      frameHeight: Number(source.frameHeight) || 0,
      fps: Math.max(1, Number(source.fps) || 8),
      loop: source.loop !== false,
      placeholder: object(source.placeholder),
      frames: array(source.frames).map(normalizeFrame),
      states: object(source.states),
      actions: object(source.actions),
    };
  }

  function normalizeAnimations(animations) {
    const source = object(animations);
    return Object.keys(source).reduce((result, key) => {
      result[key] = normalizeAnimation(key, source[key]);
      return result;
    }, {});
  }

  function normalizeMachinePart(part) {
    const source = object(part);
    return {
      id: cleanString(source.id, "part"),
      kind: cleanString(source.kind, "rect"),
      layer: cleanString(source.layer, "machines"),
      x: Number(source.x) || 0,
      y: Number(source.y) || 0,
      w: Number(source.w || source.width) || 0,
      h: Number(source.h || source.height) || 0,
      r: Number(source.r || source.radius) || 0,
      fill: cleanString(source.fill, "#7f7f7f"),
      stroke: cleanString(source.stroke, ""),
      alpha: Math.max(0, Math.min(Number(source.alpha ?? 1) || 0, 1)),
      animation: cleanString(source.animation, ""),
      role: cleanString(source.role, ""),
      metadata: object(source.metadata),
    };
  }

  function normalizeMachine(id, machine) {
    const source = object(machine);
    return {
      id: cleanString(source.id, id),
      type: cleanString(source.type, id),
      tiles: array(source.tiles).map((tile) => [Number(tile && tile[0]) || 0, Number(tile && tile[1]) || 0]),
      anchor: object(source.anchor),
      bounds: object(source.bounds),
      animation: cleanString(source.animation, ""),
      occluder: object(source.occluder),
      shadow: object(source.shadow),
      parts: array(source.parts).map(normalizeMachinePart),
      metadata: object(source.metadata),
    };
  }

  function normalizeMachines(machines) {
    const source = object(machines);
    return Object.keys(source).reduce((result, key) => {
      result[key] = normalizeMachine(key, source[key]);
      return result;
    }, {});
  }

  function normalizeManifest(input) {
    const source = object(input);
    const scene = { ...DEFAULT_SCENE, ...object(source.scene) };
    scene.width = Number(scene.width) || DEFAULT_SCENE.width;
    scene.height = Number(scene.height) || DEFAULT_SCENE.height;
    scene.tileW = Number(scene.tileW) || DEFAULT_SCENE.tileW;
    scene.tileH = Number(scene.tileH) || DEFAULT_SCENE.tileH;
    scene.originX = Number(scene.originX) || DEFAULT_SCENE.originX;
    scene.originY = Number(scene.originY) || DEFAULT_SCENE.originY;
    return freezeDeep({
      schema: cleanString(source.schema, MANIFEST_VERSION),
      id: cleanString(source.id, DEFAULT_MANIFEST),
      label: cleanString(source.label, "Factory graphics"),
      version: cleanString(source.version, "1"),
      scene,
      quality: mergeQualitySettings(source.quality),
      images: normalizeImages(source.images),
      layers: normalizeLayers(source.layers),
      animations: normalizeAnimations(source.animations),
      machines: normalizeMachines(source.machines),
      occlusion: object(source.occlusion),
      lighting: object(source.lighting),
      qa: object(source.qa),
      metadata: object(source.metadata),
    });
  }

  function normalizeBindings(input) {
    const source = object(input);
    return freezeDeep({
      schema: cleanString(source.schema, "factory-graphics-bindings.v1"),
      id: cleanString(source.id, DEFAULT_BINDINGS),
      actions: object(source.actions),
      entities: object(source.entities),
      categories: object(source.categories),
      defaults: {
        action: cleanString(source.defaults && source.defaults.action, "idle"),
        entity: cleanString(source.defaults && source.defaults.entity, "actor"),
        animation: cleanString(source.defaults && source.defaults.animation, "actor-placeholder"),
        machine: cleanString(source.defaults && source.defaults.machine, ""),
      },
      metadata: object(source.metadata),
    });
  }

  function validateManifest(manifest) {
    const normalized = normalizeManifest(manifest);
    const warnings = [];
    if (normalized.schema !== MANIFEST_VERSION) warnings.push(`Unexpected manifest schema: ${normalized.schema}`);
    if (!normalized.scene.width || !normalized.scene.height) warnings.push("Scene width and height are required.");
    Object.keys(normalized.animations).forEach((key) => {
      const animation = normalized.animations[key];
      if (animation.type === "spritesheet" && !animation.image) warnings.push(`Animation ${key} is missing an image key.`);
    });
    Object.keys(normalized.images).forEach((key) => {
      if (!normalized.images[key].src) warnings.push(`Image ${key} has no source URL.`);
    });
    return { ok: true, warnings, manifest: normalized };
  }

  function lookupBinding(table, key, fallbackKey) {
    const clean = cleanString(key, "").toLowerCase();
    const source = object(table);
    if (clean && source[clean]) return source[clean];
    if (key && source[key]) return source[key];
    if (fallbackKey && source[fallbackKey]) return source[fallbackKey];
    return null;
  }

  function resolveActionBinding(bindings, action, context) {
    const normalized = normalizeBindings(bindings);
    const key = cleanString(action || context && (context.action || context.category || context.status), normalized.defaults.action);
    const category = cleanString(context && context.category, "");
    const fromAction = lookupBinding(normalized.actions, key, normalized.defaults.action);
    const fromCategory = lookupBinding(normalized.categories, category, "");
    return freezeDeep({
      action: key,
      ...(object(fromAction)),
      ...(object(fromCategory)),
    });
  }

  function resolveEntityBinding(bindings, entity, context) {
    const normalized = normalizeBindings(bindings);
    const key = cleanString(entity || context && (context.entityType || context.type || context.kind), normalized.defaults.entity);
    const fromEntity = lookupBinding(normalized.entities, key, normalized.defaults.entity);
    return freezeDeep({
      entity: key,
      ...(object(fromEntity)),
    });
  }

  function resolveMachineDefinition(manifest, type) {
    const normalized = normalizeManifest(manifest);
    const clean = cleanString(type, "");
    return normalized.machines[clean] || normalized.machines[clean.toLowerCase()] || null;
  }

  function listRequiredImages(manifest) {
    const normalized = normalizeManifest(manifest);
    const keys = new Set();
    Object.keys(normalized.images).forEach((key) => {
      if (normalized.images[key].required) keys.add(key);
    });
    Object.keys(normalized.layers).forEach((key) => {
      const image = normalized.layers[key].image;
      if (image) keys.add(image);
    });
    Object.keys(normalized.animations).forEach((key) => {
      const image = normalized.animations[key].image;
      if (image) keys.add(image);
    });
    return Array.from(keys);
  }

  const api = {
    MANIFEST_VERSION,
    DEFAULT_MANIFEST,
    DEFAULT_BINDINGS,
    DEFAULT_QUALITY,
    DEFAULT_SCENE,
    LAYER_ORDER,
    manifestUrl,
    bindingsUrl,
    mergeQualitySettings,
    normalizeManifest,
    normalizeBindings,
    validateManifest,
    resolveActionBinding,
    resolveEntityBinding,
    resolveMachineDefinition,
    listRequiredImages,
    clone,
  };

  window.FactoryGraphicsManifest = api;
  window.FactoryGraphics = { ...(window.FactoryGraphics || {}), manifest: api };
})();
